import React, { useState } from "react";
import { GripVertical, Edit, Image as ImageIcon } from "lucide-react";
import { Button } from "../../../Components/ui/shadcn/button";
import { cn } from "../../../lib/utils";

interface BannerGroup {
    id: number;
    name?: string;
    display_level: number;
    images?: any[];
}

interface DraggableBannerListProps {
    carousels: BannerGroup[];
    onReorder: (newOrder: BannerGroup[]) => void;
    onEdit: (index: number, id: number) => void;
    emptyText?: string;
    groupLabel?: string;
    imageCountLabel?: string;
    primaryBadgeText?: string;
    showPrimaryBadge?: boolean;
}

const DraggableBannerList: React.FC<DraggableBannerListProps> = ({
    carousels,
    onReorder,
    onEdit,
    emptyText = "등록된 히어로 캐러셀 그룹이 없습니다.",
    groupLabel = "히어로 그룹",
    imageCountLabel = "이미지",
    primaryBadgeText = "메인 히어로",
    showPrimaryBadge = true,
}) => {
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
        const target = e.target as HTMLElement;
        target.classList.add("opacity-50");
    };

    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        target.classList.remove("opacity-50");
        setDraggedItemIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newCarousels = [...carousels];
        const draggedItem = newCarousels[draggedItemIndex];
        newCarousels.splice(draggedItemIndex, 1);
        newCarousels.splice(index, 0, draggedItem);

        const updatedCarousels = newCarousels.map((item, i) => ({
            ...item,
            display_level: i,
        }));

        setDraggedItemIndex(index);
        onReorder(updatedCarousels);
    };

    return (
        <div className="space-y-2 text-left">
            {carousels.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 text-sm">
                    {emptyText}
                </div>
            ) : (
                carousels.map((item, index) => (
                    <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        className={cn(
                            "flex items-center gap-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm transition-all hover:border-gray-300",
                            draggedItemIndex === index && "ring-2 ring-black bg-gray-50"
                        )}
                    >
                        <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                            <GripVertical size={20} />
                        </div>

                        <div className="w-16 h-10 bg-gray-100 rounded overflow-hidden shrink-0 border border-gray-100 flex items-center justify-center">
                            {item.images && item.images.length > 0 ? (
                                <img src={item.images[0].src} alt="Banner" className="w-full h-full object-cover" />
                            ) : (
                                <ImageIcon size={16} className="text-gray-300" />
                            )}
                        </div>

                        <div className="flex-grow">
                            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                {showPrimaryBadge && index === 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{primaryBadgeText}</span>}
                                {item.name || `${groupLabel} ${index + 1}`}
                            </div>
                            <div className="text-[10px] text-gray-500">
                                {item.images?.length || 0} {imageCountLabel} · 순서: {index + 1}
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-gray-500 hover:text-black hover:bg-gray-100 gap-1 rounded-md"
                                onClick={() => onEdit(index, item.id)}
                            >
                                <Edit size={14} />
                                <span className="text-xs">수정</span>
                            </Button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default DraggableBannerList;
